import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setViewContext, type ViewType } from '../services/viewContext';

function resolveView(pathname: string): { view: ViewType; projectId: number | null; documentId: number | null } {
  let m: RegExpMatchArray | null;

  if (pathname === '/') return { view: 'dashboard', projectId: null, documentId: null };
  if (pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return { view: 'onboarding', projectId: null, documentId: null };
  }
  if (pathname === '/login') return { view: 'login', projectId: null, documentId: null };
  if (pathname === '/register') return { view: 'register', projectId: null, documentId: null };
  if (pathname === '/request-access') return { view: 'request-access', projectId: null, documentId: null };
  if (pathname === '/tenants') return { view: 'tenants', projectId: null, documentId: null };
  if (pathname === '/users') return { view: 'users', projectId: null, documentId: null };
  if (pathname === '/tags') return { view: 'tags', projectId: null, documentId: null };
  if (pathname === '/items') return { view: 'items', projectId: null, documentId: null };
  if (pathname === '/item-details') return { view: 'item-details', projectId: null, documentId: null };
  // Exact match only — the full-screen document editor lives at this same
  // base path, and the /documents/:id routes below cover the rest.
  if (pathname === '/documents') return { view: 'document-editor', projectId: null, documentId: null };

  if ((m = pathname.match(/^\/projects\/(\d+)$/))) {
    return { view: 'project', projectId: Number(m[1]), documentId: null };
  }
  if ((m = pathname.match(/^\/projects\/(\d+)\/validation$/))) {
    return { view: 'validation', projectId: Number(m[1]), documentId: null };
  }
  if ((m = pathname.match(/^\/items\/(\d+)$/))) {
    return { view: 'item-detail', projectId: null, documentId: null };
  }
  if ((m = pathname.match(/^\/documents\/(\d+)\/presentation$/))) {
    return { view: 'document-presentation', projectId: null, documentId: Number(m[1]) };
  }
  if ((m = pathname.match(/^\/documents\/(\d+)\/entities$/))) {
    return { view: 'document-entities', projectId: null, documentId: Number(m[1]) };
  }
  if ((m = pathname.match(/^\/documents\/(\d+)$/))) {
    return { view: 'document', projectId: null, documentId: Number(m[1]) };
  }

  return { view: 'unknown', projectId: null, documentId: null };
}

/**
 * Emits a view-context change on every route change so subscribers (the AI
 * Assistant) don't need each page to wire this up individually. Pages that
 * resolve a name asynchronously (document, project) layer it in afterwards
 * via `updateViewContext` once their data loads.
 */
export function ViewContextTracker() {
  const location = useLocation();

  useEffect(() => {
    const { view, projectId, documentId } = resolveView(location.pathname);
    setViewContext({ view, projectId, documentId });
  }, [location.pathname]);

  return null;
}
